import type {PaginationMeta} from "../../../redux/service/i-service";
import type {
  GetCoHostedEvents,
  GetCoHostsApiResponse,
  ValidateCohostApiResponse,
} from "./co-hosts";

export function normalizeV2ValidateCohostResponse(data: {
  profileExists?: boolean;
  profileId?: number | null;
}): ValidateCohostApiResponse {
  return {
    profile_exists: data.profileExists ?? false,
    profile_id: data.profileId ?? 0,
  };
}

export function normalizeV2AddCoHostResponse(raw: unknown): {
  data: {coHost: {id: number; profile: {avatar: string | null}}};
} {
  const d = raw as {
    id?: number;
    userId?: number;
    emailAddress?: string;
    eventId?: number;
  };
  return {
    data: {
      coHost: {
        id: d.id ?? 0,
        profile: {avatar: null},
      },
    },
  };
}

type V2User = {
  id?: number;
  firstName?: string;
  lastName?: string;
  email?: string;
  avatar?: string | null;
  coverImage?: string | null;
  country?: string | null;
  statusId?: number;
  isSocialAuth?: boolean;
  createdAt?: string;
  updatedAt?: string;
};

function normalizeV2CoHostItem(item: {
  id?: number;
  userId?: number;
  host?: V2User | null;
  coach?: V2User | null;
  emailAddress?: string;
  eventId?: number;
  firstName?: string;
  lastName?: string;
  statusId?: number;
  statusName?: string;
}): GetCoHostsApiResponse["coHosts"][number] {
  // Use only host for the host object - coach is the event creator, not the co-host.
  // Top-level emailAddress, firstName, lastName are the main co-host details.
  const hostOnly = item.host ?? null;

  return {
    id: item.id ?? 0,
    user_id: item.userId ?? 0,
    host_id: item.host?.id ?? null,
    event_id: item.eventId ?? 0,
    createdAt: item.host?.createdAt ?? "",
    updatedAt: item.host?.updatedAt ?? "",
    first_name: item.firstName ?? item.host?.firstName ?? "",
    last_name: item.lastName ?? item.host?.lastName ?? "",
    email_address: item.emailAddress ?? item.host?.email ?? "",
    status_id: item.statusId ?? 0,
    status_name: item.statusName ?? "",
    host: hostOnly
      ? {
          id: hostOnly.id ?? 0,
          firstName: hostOnly.firstName ?? "",
          lastName: hostOnly.lastName ?? "",
          email: hostOnly.email ?? "",
          onboarding_reminder: 0,
          country: null,
          password: null,
          statusId: hostOnly.statusId ?? 0,
          isSocialAuth: hostOnly.isSocialAuth ?? false,
          avatar: hostOnly.avatar ?? null,
          cover_image: hostOnly.coverImage ?? null,
          createdAt: hostOnly.createdAt ?? "",
          updatedAt: hostOnly.updatedAt ?? "",
        }
      : null,
  };
}

export function normalizeV2FetchCoHostsResponse(data: unknown): GetCoHostsApiResponse {
  const arr = Array.isArray(data) ? data : [];
  return {
    coHosts: arr.map((item) => normalizeV2CoHostItem(item)),
  };
}

type V2CoHostedEvent = {
  id?: number;
  userId?: number;
  name?: string;
  slug?: string;
  description?: string;
  hideFromMenu?: boolean;
  requestPhoneNumber?: boolean;
  topics?: string[];
  topicType?: string;
  videoPlatform?: string;
  sessionInfo?: {
    name?: string;
    startDate?: string;
    startTime?: string;
    duration?: number;
    endTime?: string;
  }[];
  activeSession?: {
    name?: string;
    startDate?: string;
    startTime?: string;
    duration?: number;
    endTime?: string;
  };
  activeSessionId?: number;
  sessionCount?: number;
  callPriceCurrency?: string;
  callPrice?: number;
  attendees?: number;
  statusId?: number;
  statusName?: string;
  bookingQuestions?: {question?: string; required?: boolean}[] | null;
  reference?: string;
  tinyUrl?: string;
  urlRedirect?: string | null;
  isCustomRoom?: boolean;
  coverImage?: string;
  createdAt?: string;
  updatedAt?: string;
  hostProfile?: {
    id?: number;
    firstName?: string;
    lastName?: string;
    email?: string;
    username?: string;
    country?: string;
    statusId?: number;
    isSocialAuth?: boolean;
    avatar?: string | null;
    coverImage?: string | null;
    updatedAt?: string;
    statusName?: string;
  };
};

function normalizeV2CoHostedEventItem(
  item: V2CoHostedEvent,
  currentPage: number,
  perPage: number
): GetCoHostedEvents {
  const hostProfile = item.hostProfile;
  const user = hostProfile
    ? {
        id: hostProfile.id ?? 0,
        firstName: hostProfile.firstName ?? "",
        lastName: hostProfile.lastName ?? "",
        email: hostProfile.email ?? "",
        onboarding_reminder: 0,
        country: hostProfile.country ?? "",
        password: null,
        statusId: hostProfile.statusId ?? 0,
        isSocialAuth: hostProfile.isSocialAuth ?? false,
        avatar: hostProfile.avatar ?? null,
        cover_image: hostProfile.coverImage ?? null,
        createdAt: "",
        updatedAt: hostProfile.updatedAt ?? "",
        displayName: null,
      }
    : {
        id: 0,
        firstName: "",
        lastName: "",
        email: "",
        onboarding_reminder: 0,
        country: "",
        password: null,
        statusId: 0,
        isSocialAuth: false,
        avatar: null,
        cover_image: null,
        createdAt: "",
        updatedAt: "",
        displayName: null,
      };

  return {
    id: item.id ?? 0,
    userId: item.userId ?? 0,
    name: item.name ?? "",
    slug: item.slug ?? "",
    description: item.description ?? "",
    hide_from_menu: item.hideFromMenu ?? false,
    request_phone_number: item.requestPhoneNumber ?? false,
    activeSession: {
      name: item.activeSession?.name ?? "",
      startDate: item.activeSession?.startDate ?? "",
      startTime: item.activeSession?.startTime ?? "",
      duration: item.activeSession?.duration ?? 0,
      endTime: item.activeSession?.endTime ?? "",
    },
    activeSessionId: item.activeSessionId ?? 0,
    attendees: item.attendees ?? 0,
    bookingQuestions: (item.bookingQuestions ?? []).map((q) => ({
      question: q.question ?? "",
      required: q.required ?? false,
    })),
    callPrice: item.callPrice ?? 0,
    callPriceCurrency: item.callPriceCurrency ?? "",
    cover_image: item.coverImage ?? "",
    cover_images: [],
    createdAt: (item.createdAt ?? "") as unknown as Date,
    currencies: [],
    session_count: item.sessionCount ?? 0,
    session_info: (item.sessionInfo ?? []).map(
      (
        s
      ): {
        name: string;
        startDate: string;
        startTime: string;
        duration: number;
        endTime: string;
      } => ({
        name: s.name ?? "",
        startDate: s.startDate ?? "",
        startTime: s.startTime ?? "",
        duration: s.duration ?? 0,
        endTime: s.endTime ?? "",
      })
    ),
    reference: item.reference ?? "",
    roomId: "",
    tiny_url: item.tinyUrl ?? "",
    statusId: item.statusId ?? 0,
    statusName: (item.statusName ?? "Active") as "Paused" | "Active" | "In Progress",
    topic_type: (item.topicType ?? "single") as "single" | "series",
    updatedAt: (item.updatedAt ?? "") as unknown as Date,
    url_redirect: item.urlRedirect ?? undefined,
    is_custom_room: item.isCustomRoom ?? false,
    video_platform: item.videoPlatform ?? "",
    hostProfile: {
      id: hostProfile?.id ?? 0,
      userId: hostProfile?.id ?? 0,
      username: hostProfile?.username ?? "",
      description: "",
      buffer_time: 0,
      shortBio: "",
      twitter: null,
      facebook: null,
      instagram: null,
      tiktok: null,
      discord: null,
      youtube: null,
      dribble: null,
      behance: null,
      twitch: null,
      github: null,
      website: null,
      linkedin: null,
      createdAt: "",
      updatedAt: hostProfile?.updatedAt ?? "",
      user,
    },
  };
}

export function normalizeV2CoHostedEventsResponse(
  data: {
    data?: V2CoHostedEvent[];
    total?: number;
    hasNextPage?: boolean;
    hasPrevPage?: boolean;
  },
  currentPage: number,
  perPage: number
): {data: GetCoHostedEvents[]; meta: PaginationMeta} {
  const events = data.data ?? [];
  const total = data.total ?? 0;
  const totalPages = perPage > 0 ? Math.ceil(total / perPage) : 0;

  return {
    data: events.map((item) => normalizeV2CoHostedEventItem(item, currentPage, perPage)),
    meta: {
      current_page: currentPage,
      next_page: data.hasNextPage ? currentPage + 1 : 0,
      per_page: perPage,
      previous_page: data.hasPrevPage ? currentPage - 1 : 0,
      total_pages: totalPages,
    },
  };
}

/** Normalize v2 single co-hosted event response */
export function normalizeV2CoHostedEventSingleResponse(
  item: V2CoHostedEvent
): GetCoHostedEvents {
  return normalizeV2CoHostedEventItem(item, 1, 1);
}
